import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/common/modal';
import Spinner from '../../../components/common/spinner';
import Searchbar from '../../../components/common/searchbar';
import AudioPlayer from '../../../components/common/audio-player';
import { DropdownPill } from '../../../components/common/dropdown-pill';
import { Paginator } from '../../../components/common/paginator';
import { buildAudioParams, buildVideoParams } from '../utils/browse-params';
import { useBrowseListenerAudio, ListenerAudio } from '../../../hooks/useListenerAudio';
import { useVerticals } from '../../../hooks/useClients';
import { use2026TagGroups } from '../../../hooks/useTagGroups';
import { useTagMap } from '../utils/tag-helpers';
import { EmptyState, TagList } from '../components/common';

const PAGE_LIMIT = 20;

export type ContentTab = 'audio' | 'video';

export interface AddAudioSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (verticalId: number, listenerAudioId: number) => void;
  existingPairs: Array<{ vertical_id: number; listener_audio_id: number }>;
  isBusy: boolean;
  /** Which content type to browse (matches the page tab that opened the modal). */
  experienceType: ContentTab;
}

export const AddAudioSampleModal: React.FC<AddAudioSampleModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  existingPairs,
  isBusy,
  experienceType,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedVerticalId, setSelectedVerticalId] = useState<number | ''>('');
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<number | undefined>(undefined);

  const { data: verticals = [], isLoading: isLoadingVerticals } = useVerticals();
  const { data: topicTagGroups, allTags } = use2026TagGroups();
  const audioTagMap = useTagMap(allTags);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSelectedTagGroupId(undefined);
    }
  }, [isOpen]);

  const sharedFilters = useMemo(
    () => ({
      tagGroupIds: selectedTagGroupId != null ? [selectedTagGroupId] : [],
      tagIds: [] as number[],
      domainId: null as number | null,
      clientId: null as number | null,
    }),
    [selectedTagGroupId],
  );

  const params = useMemo(
    () =>
      experienceType === 'audio'
        ? buildAudioParams(page, search, sharedFilters, PAGE_LIMIT, false)
        : buildVideoParams(page, search, sharedFilters, PAGE_LIMIT, false),
    [experienceType, page, search, sharedFilters],
  );

  const { data: browseResponse, isLoading } = useBrowseListenerAudio(params, {
    enabled: isOpen,
  });

  const rows = browseResponse?.data ?? [];
  const count = browseResponse?.count ?? 0;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleAdd = (item: ListenerAudio) => {
    if (selectedVerticalId === '') return;
    if (existingPairs.some((p) => p.vertical_id === selectedVerticalId && p.listener_audio_id === item.id)) return;
    onSelect(selectedVerticalId, item.id);
  };

  const canAdd = selectedVerticalId !== '';

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isModel
      InnerComponent={
        <div
          className="inline-block w-full transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-5xl sm:align-middle"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Add sample</h3>
            <button className="text-blue-primary text-sm font-semibold" onClick={onClose} type="button">
              Close
            </button>
          </div>
          <div className="space-y-4 px-6 py-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-gray-700">Vertical</label>
              <select
                className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={selectedVerticalId}
                onChange={(e) => setSelectedVerticalId(e.target.value === '' ? '' : Number(e.target.value))}
                disabled={isLoadingVerticals}
              >
                <option value="">Select a vertical</option>
                {verticals.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-gray-700">
                {experienceType === 'audio' ? 'Listener audio' : 'Listener video'}
              </label>
              <div className="mt-2 flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                <div className="max-w-sm flex-1">
                  <Searchbar key={experienceType} initialValue={search} search={handleSearch} />
                </div>
                <DropdownPill
                  label="Filter by topic"
                  placeholder="Filter by topic"
                  options={topicTagGroups?.map((tg) => ({ value: tg.id, label: tg.name })) ?? []}
                  selectedValue={selectedTagGroupId}
                  onSelect={(value) => {
                    setSelectedTagGroupId(value as number | undefined);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : !rows.length ? (
              <EmptyState message={`No ${experienceType} content found. Try a different search or topic filter.`} />
            ) : (
              <div className="max-h-96 overflow-x-auto overflow-y-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr className="text-left text-xs uppercase text-gray-500">
                      {experienceType === 'audio' && <th className="w-32 px-4 py-3">Play</th>}
                      {experienceType === 'video' && <th className="w-32 px-4 py-3">Video</th>}
                      <th className="w-16 px-4 py-3">Image</th>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Listener</th>
                      <th className="w-24 px-4 py-3">Role ID</th>
                      <th className="px-4 py-3">Tags</th>
                      <th className="w-32 px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item) => {
                      const alreadyAdded =
                        selectedVerticalId !== '' &&
                        existingPairs.some(
                          (p) => p.vertical_id === selectedVerticalId && p.listener_audio_id === item.id,
                        );
                      const listenerName =
                        item.listener_role?.user?.display_name ?? item.listener_role?.user?.first_name ?? 'Unknown';
                      const profilePhoto = item.profile_photo_square_file_url;
                      const tagNames = (() => {
                        if (item.tag_names?.length) return item.tag_names;
                        if (audioTagMap && item.topic_tag_ids?.length) {
                          return item.topic_tag_ids
                            .map((id) => audioTagMap.get(id)?.name)
                            .filter((name): name is string => typeof name === 'string' && name.length > 0);
                        }
                        return [];
                      })();
                      const titleLabel = experienceType === 'audio' ? 'Audio' : 'Video';
                      return (
                        <tr key={item.id} className="border-b border-gray-100 text-sm text-gray-700">
                          {experienceType === 'audio' && (
                            <td className="px-4 py-3">
                              <AudioPlayer url={item.file_url} isTimeDisplay audioDuration={item.duration} />
                            </td>
                          )}
                          {experienceType === 'video' && (
                            <td className="px-4 py-3">
                              {item.video_id ? (
                                <a
                                  href={`https://vimeo.com/${item.video_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-primary text-xs font-semibold"
                                >
                                  Watch
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            {profilePhoto ? (
                              <img
                                src={profilePhoto}
                                alt={listenerName}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-500">
                                —
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold">{item.title ?? `${titleLabel} ${item.id}`}</div>
                            <div className="text-xs text-gray-500">
                              #{item.id}
                              {experienceType === 'video' && item.video_id && (
                                <>
                                  {' · '}
                                  <a
                                    href={`https://vimeo.com/${item.video_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-primary"
                                  >
                                    Watch
                                  </a>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{listenerName}</td>
                          <td className="px-4 py-3 font-mono text-xs">{item.listener_role_id}</td>
                          <td className="px-4 py-3">
                            <TagList tags={tagNames} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              className={`rounded-md px-3 py-1 text-xs font-semibold ${
                                alreadyAdded || !canAdd
                                  ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                                  : 'bg-blue-primary text-white'
                              }`}
                              onClick={() => handleAdd(item)}
                              disabled={isBusy || alreadyAdded || !canAdd}
                            >
                              {alreadyAdded ? 'Added' : 'Add'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {count > PAGE_LIMIT && (
              <div className="mt-4 flex justify-end">
                <Paginator count={count} limit={PAGE_LIMIT} currentPage={page} handlePageChange={setPage} />
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};
